import { useEffect, useState, useCallback } from "react";
import { Power, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { ago } from "./mgrShared";

export const EngineControl = () => {
  const [s, setS] = useState(null);
  const [confirm, setConfirm] = useState(null); // "on" | "off"
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setS((await api.get("/manager/engine")).data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  const apply = async () => {
    setBusy(true);
    try {
      await api.post(`/manager/engine/${confirm}`, { reason: reason || null });
      toast.success(confirm === "on" ? "Engine ON — redeploying & warming connections…" : "Engine OFF — new trades blocked, positions untouched");
      setConfirm(null); setReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!s) return <div className="skeleton h-16" />;
  const on = s.enabled;
  return (
    <div className={`card-dark p-4 flex flex-wrap items-center justify-between gap-4 border ${on ? "border-[#00E676]/30" : "border-[#FF3B30]/40"}`} data-testid="engine-control">
      <div className="flex items-center gap-4">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${on ? "bg-[#00E676]/15" : "bg-[#FF3B30]/15"}`}>
          {s.transition_running ? <Loader2 size={18} className="animate-spin text-[#00F0FF]" /> : <Power size={18} className={on ? "text-[#00E676]" : "text-[#FF3B30]"} />}
        </div>
        <div>
          <p className="label-xs">MetaApi Trading Engine</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`w-2 h-2 rounded-full ${on ? "bg-[#00E676] shadow-[0_0_8px_#00E676]" : "bg-[#FF3B30]"}`} />
            <span className={`font-display text-lg ${on ? "text-[#00E676]" : "text-[#FF3B30]"}`} data-testid="engine-state">{on ? "ON" : "OFF"}</span>
            <span className="text-xs text-gray-500" data-testid="engine-sub">
              {s.transition_running ? (on ? "Redeploying & warming up…" : "Undeploying MetaApi workloads…")
                : on ? `${s.transition === "ready" || s.transition === undefined ? "Connected / Ready" : s.transition} · ${s.live_accounts}/${s.connected_accounts} accounts live · ${s.warm_connections} warm`
                : `Trading execution disabled${s.undeploy_on_off ? " · MetaApi workloads undeployed (no per-account hosting charge)" : ""}`}
            </span>
          </div>
          <p className="text-[10px] text-gray-600 mt-1">Last health check {ago(s.last_health_at || s.changed_at)}{s.changed_by_name ? ` · changed by ${s.changed_by_name} ${ago(s.changed_at)}` : ""}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {on && !s.transition_running && (
          <button data-testid="engine-warm-btn" onClick={async () => { try { await api.post("/manager/engine/warm"); toast.success("Warm-up started"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } }} className="btn-outline px-3 py-2 text-xs">Warm up</button>
        )}
        <button data-testid="engine-toggle-btn" disabled={s.transition_running} onClick={() => setConfirm(on ? "off" : "on")}
          className={`px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-40 ${on ? "bg-[#FF3B30]/15 border border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30]/25" : "btn-primary"}`}>
          {on ? "Turn OFF" : "Turn ON"}
        </button>
      </div>
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setConfirm(null)} data-testid="engine-confirm-modal">
          <div className="card-dark w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-white"><ShieldAlert size={16} className={confirm === "off" ? "text-[#FF3B30]" : "text-[#00E676]"} /> Turn {confirm === "off" ? "off" : "on"} MetaApi trading engine?</div>
            <p className="text-sm text-gray-400">
              {confirm === "off" ? "New trades will not be executed while the engine is OFF. Existing MT5 positions will remain open. MetaApi workloads will be undeployed to stop per-account hosting charges (credentials, mapping and history are preserved)."
                : "The system will reconnect/redeploy required MetaApi workloads and warm up before accepting new trades (about 1–3 minutes per account)."}
            </p>
            <input data-testid="engine-reason" className="input-dark text-sm" placeholder="Reason (optional, saved to audit log)" value={reason} onChange={(e) => setReason(e.target.value)} />
            <div className="flex justify-end gap-2">
              <button data-testid="engine-cancel" onClick={() => setConfirm(null)} className="btn-outline px-4 py-2 text-sm">Cancel</button>
              <button data-testid="engine-confirm" disabled={busy} onClick={apply} className={`px-4 py-2 rounded-lg text-sm ${confirm === "off" ? "bg-[#FF3B30] text-white" : "btn-primary"}`}>{busy ? "…" : confirm === "off" ? "Turn Off" : "Turn On"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
